import React from "react";
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from "react-error-boundary";
import { Button } from "@/components/ui"; // Using your reusable Button

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

function Fallback({ error, resetErrorBoundary }: FallbackProps) {
    console.error("App crashed:", error);

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-[#f6f7fb] text-center px-6 font-sans">
            {/* Error Icon */}
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-6 shadow-sm border border-red-100">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ff424f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-100 mb-3">
                Something went wrong
            </h2>

            {/* Message */}
            <p className="text-gray-80 mb-8 max-w-md text-sm leading-6">
                An unexpected error occurred while processing your request. 
                Please try refreshing the page or contact support if the issue persists.
            </p>

            {/* Reload Button */}
            <Button
                size="lg"
                variant="primary"
                className="min-w-[180px]"
                onClick={() => {
                    // Attempt to recover by resetting the boundary
                    resetErrorBoundary();
                    // Optionally force a hard reload to clear global state issues:
                    // window.location.reload(); 
                }}
            >
                Reload App
            </Button>
        </div>
    );
}

/* ------------------------------------
   🛡️ Error Boundary Wrapper
------------------------------------- */
export const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
    return (
        <ReactErrorBoundary
            FallbackComponent={Fallback}
            onError={(error:any, info:any) => {
                // Log to your error reporting service (e.g., Sentry, LogRocket)
                console.error("Error Boundary Catch:", error, info);
            }}
            onReset={() => {
                // Reset the state of your app here if using Global State (Redux/Zustand)
                // This ensures the user doesn't get stuck in the same error loop
                console.log("Error Boundary Resetting...");
            }}
        >
            {children}
        </ReactErrorBoundary>
    );
};